import { Formik, FormikHelpers, Form } from "formik";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { FormikCheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { FormInput } from "@/core/components/form-input";
import { SignupSourceSelector } from "@/core/components/signup-source-selector";
import { Button } from "@/core/components/ui/button";
import { PhoneInput } from "@/core/components/ui/phone-input/phone-input";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { formatDate } from "@/core/utils/date";
import { normalizeSignupSource } from "@/core/utils/form";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { CustomerWithReferralsAndPayouts } from "@/customer/customer";
import { useUpsertCustomer } from "@/customer/query";

interface CustomerFormValues {
  name: string;
  phone_number: string;
  signup_source_id: string;
  signup_source_type: string;
  onboard: boolean;
  spiffable: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string(),
  phone_number: Yup.string().required("Phone number is required"),
  signup_source_id: Yup.string(),
  signup_source_type: Yup.string(),
  spiffable: Yup.boolean(),
});

interface DetailsTabProps {
  businessId: string;
  initialData: CustomerWithReferralsAndPayouts | null;
  isNewMode: boolean;
  canChangeAttribution: boolean;
}

export const DetailsTab = ({
  businessId,
  initialData,
  isNewMode,
  canChangeAttribution,
}: DetailsTabProps) => {
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { mutate: upsertCustomer } = useUpsertCustomer(businessId);

  const initialValues = useMemo<CustomerFormValues>(
    () => ({
      name: initialData?.name || "",
      phone_number: formatPhoneNumber(initialData?.phone_number) || "",
      signup_source_id: initialData?.signup_source?.id || "none",
      signup_source_type: initialData?.signup_source?.class_name || "",
      onboard: false,
      spiffable: false,
    }),
    [initialData],
  );

  const handleSubmit = useCallback(
    async (
      values: CustomerFormValues,
      { resetForm }: FormikHelpers<CustomerFormValues>,
    ) => {
      // Ensure both signup source fields are set or neither is set
      const normalizedSignupSource = normalizeSignupSource(
        values.signup_source_id,
        values.signup_source_type,
      );

      return upsertCustomer(
        {
          ...values,
          id: initialData?.id,
          ...normalizedSignupSource,
          status: CUSTOMER_STATUS.ACTIVE,
          onboard: isNewMode && values.onboard,
          spiffable:
            isNewMode && normalizedSignupSource.signup_source_id
              ? values.spiffable
              : false,
        },
        {
          onSuccess: (updatedCustomer) => {
            resetForm({ values });

            if (isNewMode) {
              updateSidebar({
                selectedItemId: updatedCustomer.id,
                mode: SIDEBAR_MODES.view,
              });
            } else {
              updateSidebar({
                selectedItemId: updatedCustomer.id,
              });
            }
            toast.success(
              `Customer ${isNewMode ? "created" : "updated"} successfully`,
            );
          },
          onError: (error) => {
            toast.error(
              `Failed to ${isNewMode ? "create" : "update"} advocate`,
              {
                description:
                  error instanceof Error ? error.message : "Please try again",
              },
            );
          },
        },
      );
    },
    [initialData, isNewMode, updateSidebar, upsertCustomer],
  );

  return (
    <ScrollArea className="h-full pr-2">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, dirty, resetForm }) => (
          <Form className="w-full pb-6">
            <Stack gap="lg" fullWidth>
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={(e) => setFieldValue("name", e.target.value)}
                fullWidth
              />

              <PhoneInput
                name="phone_number"
                value={values.phone_number}
                onChange={(value) => setFieldValue("phone_number", value)}
                disabled={!isNewMode}
                required
                fullWidth
                disabledTooltip={
                  <>
                    {" "}
                    Contact support at{" "}
                    <span className="font-semibold">support@volca.ai</span> to
                    change a customer phone number
                  </>
                }
              />

              {isNewMode && (
                <>
                  <FormikCheckboxWithLabel
                    id="onboard"
                    label="Send advocate program sign-up SMS"
                    checked={values.onboard}
                    onCheckedChange={(checked) =>
                      setFieldValue("onboard", !!checked)
                    }
                    containerClassName="pl-px"
                  />
                  <FormikCheckboxWithLabel
                    id="spiffable"
                    label="Create spiff for attributed user?"
                    checked={values.spiffable}
                    onCheckedChange={(checked) =>
                      setFieldValue("spiffable", !!checked)
                    }
                    disabled={
                      values.signup_source_id === "none" ||
                      !values.signup_source_id
                    }
                    containerClassName="pl-px"
                  />
                </>
              )}

              <SignupSourceSelector
                id={`signup-source-details-${initialData?.id}`}
                businessId={businessId}
                selectedSourceId={values.signup_source_id}
                selectedSourceType={values.signup_source_type}
                onSourceChange={(sourceId, sourceType) => {
                  setFieldValue("signup_source_id", sourceId);
                  setFieldValue("signup_source_type", sourceType);
                }}
                disabled={!canChangeAttribution}
                disabledTooltip={
                  !canChangeAttribution
                    ? "Only owners can change customer attribution"
                    : undefined
                }
              />

              {initialData && !isNewMode && (
                <>
                  <Stack gap="xs" fullWidth>
                    <Typography variant="subtitleW3" textColor="tertiary">
                      Created At
                    </Typography>
                    <Typography variant="bodyW1">
                      {formatDate(initialData.created_at)}
                    </Typography>
                  </Stack>

                  {initialData.email && (
                    <Stack gap="xs" fullWidth>
                      <Typography variant="subtitleW3" textColor="tertiary">
                        Email
                      </Typography>
                      <Typography variant="bodyW1">
                        {initialData.email}
                      </Typography>
                    </Stack>
                  )}
                </>
              )}

              <Stack direction="row" className="mt-4">
                <Button type="submit" size="sm" disabled={!dirty}>
                  {isNewMode ? "Create Customer" : "Save Changes"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!dirty}
                  onClick={() => resetForm()}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </ScrollArea>
  );
};
